import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Upload, FileText, Calendar, AlertCircle, Loader, Smartphone, ZoomIn, ZoomOut, RefreshCw } from 'lucide-react';
import DatePickerField from './UI/DatePickerField';
import toast from '../utils/toast';
import { staffAPI } from '../services/api';
import api from '../services/api';
import { isNSLeaveType } from '../constants/leaveTypes';
import { formatLeaveDays, pluralizeDays } from '../utils/numberUtils';
import Portal from './UI/Portal';
import ConfirmModal from './UI/ConfirmModal';
import { InfoBadge } from './UI/InfoBadge';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSafeDiscardAndClose } from '../hooks/useSafeDiscardAndClose';
import { useImageViewer } from '../hooks/useImageViewer';
import MobileVerificationTabs from './UI/MobileVerificationTabs';
import VerificationPanelHeader from './UI/VerificationPanelHeader';
import LeaveExcludedDaysCallout from './Leave/LeaveExcludedDaysCallout';
import PdfViewer from './UI/PdfViewer';
import FormSelect from './UI/FormSelect';
import { toLocalISODate, parseLocalISOToDate } from '../utils/dateUtils';


const NSLeaveApplicationModal = ({
  isOpen,
  onClose,
  onSubmit,
  editingLeave = null,
  availableLeaveTypes = [],
  leaveTypeOptions = [],
  selectedLeaveTypeId = '',
  onChangeLeaveType
}) => {
  // Upload state: 'idle' | 'uploading' | 'ocr_processing' | 'uploaded' | 'error'
  const [uploadState, setUploadState] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [documentData, setDocumentData] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isDiscarding, safeDiscardAndClose } = useSafeDiscardAndClose();
  
  // OCR processing state
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const ocrTimeoutRef = useRef(null);
  const ocrAbortControllerRef = useRef(null);
  
  // Document blob URL for PDF preview (reusing DocumentVerificationModal pattern)
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState(null);
  const documentBlobUrlRef = useRef(null);
  const documentAbortControllerRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    leave_type_id: '',
    activity_type: 'In-Camp Training (ICT)', // Default to ICT
    other_activity_type: '',
    serial_number: '',
    issue_date: '',
    leave_start_date: '',
    leave_end_date: '',
    leave_start_time_period: 'AM',
    leave_end_time_period: 'PM',
    leave_reason: 'National Service Leave'
  });
  
  // Leave days calculation
  const [calculatedLeaveDays, setCalculatedLeaveDays] = useState(0);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState(null);
  const [excludedLeaveDates, setExcludedLeaveDates] = useState([]);
  const calculationAbortControllerRef = useRef(null);
  
  // UI state
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('document');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // Form validation errors
  const [validationErrors, setValidationErrors] = useState({
    activity_type: null,
    other_activity_type: null,
    leave_start_date: null,
    leave_end_date: null,
    serial_number: null,
    issue_date: null
  });
  
  const viewer = useImageViewer();
  
  // NS Activity Types
  const nsActivityTypes = [
    'ICT (In-Camp Training)',
    'Training / Exercise (non-ICT)',
    'Meeting / Briefing',
    'Medical (screening / appointment)',
    'Fitness (IPPT / NS FIT / RT)',
    'Others'
  ];
  
  // Check if editing and has existing document
  useEffect(() => {
    if (isOpen && editingLeave) {
      // Pre-fill form data
      setFormData(prev => ({
        ...prev,
        leave_type_id: editingLeave.leave_type_id?.id || editingLeave.leave_type_id || '',
        activity_type: editingLeave.activity_type || '',
        other_activity_type: editingLeave.other_activity_type || '',
        serial_number: editingLeave.serial_number || '',
        issue_date: editingLeave.issue_date || '',
        leave_start_date: editingLeave.leave_start_date || '',
        leave_end_date: editingLeave.leave_end_date || editingLeave.leave_start_date || '',
        leave_start_time_period: editingLeave.leave_start_time_period || 'AM',
        leave_end_time_period: editingLeave.leave_end_time_period || 'PM',
        leave_reason: editingLeave.leave_reason || 'National Service Leave'
      }));
      
      // If editing, find associated DocumentApproval by leave_application_id
      if (editingLeave.id) {
        setUploadState('uploaded');
        // Load existing document preview
        const fetchDocumentPreview = async () => {
          try {
            // Find DocumentApproval linked to this leave application
            const response = await staffAPI.getAllDocumentApprovals({
              leave_application_id: editingLeave.id,
              document_type: 'ns_call_up'
            });
            
            const docApprovals = Array.isArray(response.data?.results) 
              ? response.data.results 
              : Array.isArray(response.data) 
                ? response.data 
                : response.data ? [response.data] : [];
            
            const docApproval = docApprovals.find(d => 
              d.document_type === 'ns_call_up' && 
              (d.leave_application_id === editingLeave.id || d.leave_application_id === String(editingLeave.id))
            ) || docApprovals[0];
            
            if (docApproval?.document_file) {
              const filePath = typeof docApproval.document_file === 'string' 
                ? docApproval.document_file 
                : docApproval.document_file?.name || docApproval.document_file;
              
              if (filePath) {
                const isImage = !!filePath.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/);
                setDocumentData({
                  id: docApproval.id,
                  filePath: filePath,
                  previewUrl: null,
                  type: isImage ? 'image' : 'pdf',
                  fileName: docApproval.file_name || 'NS Call-up Order'
                });
              }
            }
          } catch (error) {
            console.error('Error fetching document preview:', error);
            // If document not found, allow user to upload new one
            setUploadState('idle');
          }
        };
        fetchDocumentPreview();
      }
    } else if (isOpen && !editingLeave) {
      // Reset state for new application
      setUploadState('idle');
      setFormData({
        leave_type_id: '',
        activity_type: 'In-Camp Training (ICT)', // Default to ICT
        other_activity_type: '',
        serial_number: '',
        issue_date: '',
        leave_start_date: '',
        leave_end_date: '',
        leave_start_time_period: 'AM',
        leave_end_time_period: 'PM',
        leave_reason: 'National Service Leave'
      });
      setDocumentData(null);
      setUploadError(null);
      setCalculatedLeaveDays(0);
      setExcludedLeaveDates([]);
    }
  }, [isOpen, editingLeave]);
  
  // Find NS leave type
  useEffect(() => {
    if (isOpen && availableLeaveTypes.length > 0) {
      const nsLeaveType = availableLeaveTypes.find(lt => 
        isNSLeaveType(lt.id) || 
        (lt.leave_type_name || '').toLowerCase().includes('national service')
      );
      if (nsLeaveType) {
        setFormData(prev => ({ ...prev, leave_type_id: String(nsLeaveType.id) }));
      }
    }
  }, [isOpen, availableLeaveTypes]);
  
  // Calculate leave days with debounce
  useEffect(() => {
    // Cancel previous calculation if exists
    if (calculationAbortControllerRef.current) {
      calculationAbortControllerRef.current.abort();
    }
    
      if (!formData.leave_start_date || !formData.leave_end_date || !formData.leave_type_id) {
        setCalculatedLeaveDays(0);
        setExcludedLeaveDates([]);
      setCalcError(null);
        return;
      }
      
      const selectedType = availableLeaveTypes.find(t => String(t.id) === String(formData.leave_type_id));
      if (!selectedType) {
        setCalculatedLeaveDays(0);
        setExcludedLeaveDates([]);
      setCalcError(null);
        return;
      }
      
    // Debounce calculation by 500ms
    const timeoutId = setTimeout(async () => {
      calculationAbortControllerRef.current = new AbortController();
      setCalcLoading(true);
      setCalcError(null);
      
      try {
        const payload = {
          start_date: formData.leave_start_date,
          end_date: formData.leave_end_date,
          leave_type_name: selectedType.leave_type_name
        };
        const resp = await staffAPI.calculateLeaveDays(payload);
        let days = resp?.data?.calculation?.mom_compliant_days ?? 0;
        
        // Apply AM/PM adjustments
        if (formData.leave_start_time_period === 'PM') days -= 0.5;
        if (formData.leave_end_time_period === 'AM') days -= 0.5;
        
        setCalculatedLeaveDays(Math.max(0, Number(days)));
        setExcludedLeaveDates(resp?.data?.calculation?.excluded_dates || []);
        setCalcError(null);
      } catch (error) {
        if (error.name === 'AbortError') {
          return; // Calculation was cancelled
        }
        console.error('Error calculating leave days:', error);
        setCalcError(error.response?.data?.error || 'Failed to calculate leave days');
        setCalculatedLeaveDays(0);
        setExcludedLeaveDates([]);
        toast.error('Failed to calculate leave days. Please check your dates.');
      } finally {
        setCalcLoading(false);
      }
    }, 500);
    
    return () => {
      clearTimeout(timeoutId);
      if (calculationAbortControllerRef.current) {
        calculationAbortControllerRef.current.abort();
      }
    };
  }, [formData.leave_start_date, formData.leave_end_date, formData.leave_start_time_period, formData.leave_end_time_period, formData.leave_type_id, availableLeaveTypes]);
  
  const loadDocumentBlob = async (filePath) => {
    if (documentAbortControllerRef.current) {
      documentAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    documentAbortControllerRef.current = abortController;

    try {
      setLoadingDocument(true);
      setDocumentError(null);

      if (documentBlobUrlRef.current) {
        URL.revokeObjectURL(documentBlobUrlRef.current);
        documentBlobUrlRef.current = null;
      }

      if (!filePath) throw new Error('Missing document file path');

      let apiPath = filePath;
      if (filePath.includes('/view/')) {
        const urlParts = filePath.split('/view/');
        apiPath = urlParts[urlParts.length - 1];
      } else if (filePath.startsWith('/documents/view/')) {
        apiPath = filePath.replace('/documents/view/', '');
      }

      const response = await api.get(`/documents/view/${encodeURIComponent(apiPath)}/`, {
        responseType: 'blob',
        signal: abortController.signal
      });

      if (abortController.signal.aborted) return;

      const fileExt = (apiPath.split('.').pop() || '').toLowerCase();
      const inferredType = response.headers?.['content-type']
        || (fileExt === 'pdf' ? 'application/pdf'
            : ['jpg', 'jpeg', 'png', 'gif'].includes(fileExt) ? `image/${fileExt === 'jpg' ? 'jpeg' : fileExt}`
            : 'application/octet-stream');
      const blob = new Blob([response.data], { type: inferredType });
      const blobUrl = URL.createObjectURL(blob);

      if (documentBlobUrlRef.current) URL.revokeObjectURL(documentBlobUrlRef.current);
      documentBlobUrlRef.current = blobUrl;
      setDocumentBlobUrl(blobUrl);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading document:', error);
      setDocumentError('Failed to load document preview');
    } finally {
      setLoadingDocument(false);
    }
  };

  // Load blob when documentData.filePath changes (for both images and PDFs).
  // Skip if previewUrl is a data URL (local file from FileReader after upload).
  useEffect(() => {
    const filePath = documentData?.filePath;
    const hasLocalPreview = documentData?.previewUrl?.startsWith('data:');

    if (isOpen && filePath && !hasLocalPreview) {
      loadDocumentBlob(filePath);
    }

    return () => {
      if (documentBlobUrlRef.current) {
        URL.revokeObjectURL(documentBlobUrlRef.current);
        documentBlobUrlRef.current = null;
      }
      if (documentAbortControllerRef.current) {
        documentAbortControllerRef.current.abort();
      }
    };
  }, [documentData?.filePath, isOpen]);
  
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (calculationAbortControllerRef.current) {
        calculationAbortControllerRef.current.abort();
      }
      if (ocrTimeoutRef.current) {
        clearTimeout(ocrTimeoutRef.current);
      }
      if (ocrAbortControllerRef.current) {
        ocrAbortControllerRef.current.abort();
      }
    };
  }, []);
  
  // File validation
  const validateFile = (file) => {
    if (!file) return 'No file selected';
    if (file.size > 5 * 1024 * 1024) return 'File size exceeds 5MB limit';
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Only PDF, JPG, and PNG files are allowed.';
    }
    return null;
  };
  
  // Handle file selection
  const handleFileSelect = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    
    // Cancel previous upload if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Cancel previous OCR if exists
    if (ocrTimeoutRef.current) {
      clearTimeout(ocrTimeoutRef.current);
      ocrTimeoutRef.current = null;
    }
    if (ocrAbortControllerRef.current) {
      ocrAbortControllerRef.current.abort();
    }
    
    setUploadState('uploading');
    setUploadProgress(0);
    setUploadError(null);
    setOcrProcessing(false);
    
    abortControllerRef.current = new AbortController();
    
    try {
      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload with progress tracking
      const response = await staffAPI.uploadNSDocument(file);
      
      if (response.data.success) {
        // Build preview URL from file_path
        const filePath = response.data.file_path;
        const fileType = response.data.file_type || file.type;
        const isImage = fileType.startsWith('image/');
        const isPdf = fileType === 'application/pdf';
        const documentApprovalId = response.data.document_approval_id;
        const staffId = response.data.staff_id; // Get from response if available
        const reusedExisting = response.data.reused_existing === true;
        
        // Set document data first (for preview)
        if (isImage) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setDocumentData({
              id: documentApprovalId,
              file: file,
              previewUrl: e.target.result,
              filePath: filePath,
              type: 'image',
              fileName: response.data.file_name || file.name,
              reusedExisting
            });
          };
          reader.readAsDataURL(file);
        } else if (isPdf && filePath) {
          // For PDFs, store filePath - PDF viewer will load it
          setDocumentData({
            id: documentApprovalId,
            file: file,
            previewUrl: null, // Will be loaded by PDF viewer
            filePath: filePath,
            type: 'pdf',
            fileName: response.data.file_name || file.name,
            reusedExisting
          });
        } else {
          // Fallback
          setDocumentData({
            id: documentApprovalId,
            file: file,
            previewUrl: filePath ? `/documents/view/${filePath}/` : null,
            filePath: filePath,
            type: isPdf ? 'pdf' : 'image',
            fileName: response.data.file_name || file.name,
            reusedExisting
          });
        }
        
        // Trigger n8n OCR processing and populate form with extracted data
        if (documentApprovalId) {
          // Set OCR processing state
          setOcrProcessing(true);
          setUploadState('ocr_processing');
          
          // Show persistent loading toast
          const loadingToast = toast.loading('🔄 Extracting details from document...', { 
            duration: Infinity // Keep toast until dismissed
          });
          
          // Set 30-second timeout
          ocrTimeoutRef.current = setTimeout(() => {
            // Timeout reached - open modal with empty form
            toast.dismiss(loadingToast);
            toast.warning('OCR processing timed out. You can manually fill in the form.', { duration: 5000 });
            setOcrProcessing(false);
            setUploadState('uploaded');
            if (ocrAbortControllerRef.current) {
              ocrAbortControllerRef.current.abort();
            }
          }, 30000); // 30 seconds
          
          // Cancel previous OCR request if exists
          if (ocrAbortControllerRef.current) {
            ocrAbortControllerRef.current.abort();
          }
          ocrAbortControllerRef.current = new AbortController();
          
          staffAPI.sendNSCallUpToN8N(documentApprovalId, file, staffId)
            .then((ocrResponse) => {
              // Clear timeout since OCR completed
              if (ocrTimeoutRef.current) {
                clearTimeout(ocrTimeoutRef.current);
                ocrTimeoutRef.current = null;
              }
              
              console.log('OCR response received:', ocrResponse);
              
              // Check if OCR was successful
              if (!ocrResponse?.data?.success) {
                // OCR failed but document was uploaded
                toast.dismiss(loadingToast);
                setOcrProcessing(false);
                setUploadState('uploaded');
                if (ocrResponse?.data?.warning) {
                  toast.error(ocrResponse.data.warning, { duration: 5000 });
                } else {
                  toast.warning('Document uploaded, but OCR processing is unavailable. You can manually fill in the form.', { duration: 5000 });
                }
                return;
              }
              
              // Parse OCR data from n8n response
              // n8n response structure: { success: true, n8n_response: { ocr_data: {...} } }
              const n8nResponse = ocrResponse?.data?.n8n_response || ocrResponse?.data || {};
              const rawPayload = Array.isArray(n8nResponse) ? n8nResponse[0] : n8nResponse;
              const rawOcrData = rawPayload?.ocr_data || rawPayload?.extracted_data || rawPayload || {};
              
              // Normalize OCR keys: strip "ocr_" prefix if present so downstream code works with either format
              const ocrData = {};
              for (const [key, value] of Object.entries(rawOcrData)) {
                const normalizedKey = key.startsWith('ocr_') ? key.slice(4) : key;
                ocrData[normalizedKey] = value;
              }
              
              console.log('Parsed OCR data:', ocrData);
              
              // Helper function to format dates for input fields (YYYY-MM-DD)
              const formatDateForInput = (dateString) => {
                if (!dateString) return '';
                try {
                  // Try DD/MM/YYYY or DD-MM-YYYY first (common OCR format) before new Date()
                  // to avoid US-format MM/DD/YYYY misinterpretation
                  const slashDashParts = dateString.split(/[-\/]/);
                  if (slashDashParts.length === 3) {
                    const day = parseInt(slashDashParts[0], 10);
                    const month = parseInt(slashDashParts[1], 10);
                    const year = parseInt(slashDashParts[2], 10);
                    if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
                      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    }
                  }
                  
                  // Try parsing DD MMM YYYY format (like "02 Dec 2025")
                  const parts = dateString.trim().split(/\s+/);
                  if (parts.length === 3) {
                    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const monthIndex = months.findIndex(m => m === parts[1]);
                    if (monthIndex !== -1) {
                      const newDate = new Date(parseInt(parts[2]), monthIndex, parseInt(parts[0]));
                      if (!isNaN(newDate.getTime())) {
                        const year = newDate.getFullYear();
                        const month = String(newDate.getMonth() + 1).padStart(2, '0');
                        const day = String(newDate.getDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                      }
                    }
                  }

                  // Fallback: ISO format YYYY-MM-DD or similar
                  const date = new Date(dateString);
                  if (!isNaN(date.getTime())) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  }
                  return '';
                } catch (error) {
                  console.error('Error formatting date:', error);
                  return '';
                }
              };
              
              // Map OCR data to form fields
              const updates = {};
              
              // Serial number
              if (ocrData.ns_callup_serial_number) {
                updates.serial_number = String(ocrData.ns_callup_serial_number).trim();
              }
              
              // Issue date
              if (ocrData.ns_callup_issue_date) {
                updates.issue_date = formatDateForInput(ocrData.ns_callup_issue_date);
              }
              
              // Activity type - OCR returns exact dropdown value, so use as-is if it matches
              if (ocrData.ns_callup_activity) {
                const ocrActivity = String(ocrData.ns_callup_activity).trim();
                // Check if OCR activity matches one of the dropdown options
                if (nsActivityTypes.includes(ocrActivity)) {
                  updates.activity_type = ocrActivity;
                } else {
                  // If doesn't match, set to "Others" and put OCR value in other_activity_type
                  updates.activity_type = 'Others';
                  updates.other_activity_type = ocrActivity;
                }
              }
              
              // Start date
              if (ocrData.ns_callup_start_date) {
                updates.leave_start_date = formatDateForInput(ocrData.ns_callup_start_date);
              }
              
              // End date
              if (ocrData.ns_callup_end_date) {
                updates.leave_end_date = formatDateForInput(ocrData.ns_callup_end_date);
              }
              
              // Time periods (should be "AM" or "PM" from OCR)
              if (ocrData.ns_callup_start_time_period && ['AM', 'PM'].includes(ocrData.ns_callup_start_time_period)) {
                updates.leave_start_time_period = ocrData.ns_callup_start_time_period;
              }
              
              if (ocrData.ns_callup_end_time_period && ['AM', 'PM'].includes(ocrData.ns_callup_end_time_period)) {
                updates.leave_end_time_period = ocrData.ns_callup_end_time_period;
              }
              
              // Update form with extracted data
              if (Object.keys(updates).length > 0) {
                setFormData(prev => ({ ...prev, ...updates }));
                
                // Dismiss loading toast and show success
                toast.dismiss(loadingToast);
                toast.success(
                  '✅ Details extracted successfully\nForm fields have been auto-filled',
                  { duration: 4000 }
                );
              } else {
                // No OCR data extracted
                toast.dismiss(loadingToast);
                toast.warning('Document uploaded, but no data could be extracted. Please fill in the form manually.', { duration: 4000 });
              }
              
              // Mark OCR as complete and show form
              setOcrProcessing(false);
              setUploadState('uploaded');
            })
            .catch((error) => {
              // Clear timeout on error
              if (ocrTimeoutRef.current) {
                clearTimeout(ocrTimeoutRef.current);
                ocrTimeoutRef.current = null;
              }
              
              console.error('Failed to trigger OCR:', error);
              toast.dismiss(loadingToast);
              setOcrProcessing(false);
              setUploadState('uploaded');
              
              // Show error but allow manual entry
              if (error.name === 'AbortError' || error.name === 'CanceledError') {
                // Request was cancelled (e.g., timeout or new upload)
                return;
              }
              toast.warning('OCR processing failed. You can manually fill in the form.', { duration: 5000 });
            });
        }
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return; // Upload was cancelled
      }
      console.error('Upload error:', error);
      setUploadError(error.response?.data?.error || error.message || 'Upload failed');
      setUploadState('error');
      toast.error('Failed to upload document. Please try again.');
    }
  };
  
  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };
  
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard against double-submission (rapid double-click / slow network).
    // Without this, each click created a fresh leave application + linked
    // document, flooding HR's Pending Verification queue with duplicates.
    if (isSubmitting) return;

    // Reset validation errors
    setValidationErrors({
      activity_type: null,
      other_activity_type: null,
      leave_start_date: null,
      leave_end_date: null,
      serial_number: null,
      issue_date: null
    });
    
    // Validation
    let hasErrors = false;
    const newErrors = {
      activity_type: null,
      other_activity_type: null,
      leave_start_date: null,
      leave_end_date: null,
      serial_number: null,
      issue_date: null
    };
    
    if (uploadState !== 'uploaded' || !documentData) {
      toast.error('Please upload NS Call-up Order document first');
      return;
    }
    
    // NS Activity Type is now optional - no validation needed
    
    if (formData.activity_type === 'Others' && !formData.other_activity_type?.trim()) {
      newErrors.other_activity_type = 'Please specify the activity type';
      hasErrors = true;
    }
    
    if (!formData.leave_start_date) {
      newErrors.leave_start_date = 'Please select start date';
      hasErrors = true;
    }
    
    if (!formData.leave_end_date) {
      newErrors.leave_end_date = 'Please select end date';
      hasErrors = true;
    }
    
    if (!formData.serial_number?.trim()) {
      newErrors.serial_number = 'Please enter Call-up Serial Number';
      hasErrors = true;
    }
    
    if (!formData.issue_date) {
      newErrors.issue_date = 'Please select Call-up Issue Date';
      hasErrors = true;
    }
    
    // Validate issue_date is before or equal to start_date
    if (formData.issue_date && formData.leave_start_date) {
      const issueDate = parseLocalISOToDate(formData.issue_date);
      const startDate = parseLocalISOToDate(formData.leave_start_date);
      
      if (issueDate && startDate && issueDate > startDate) {
        newErrors.issue_date = 'Issue date must be before or equal to the activity start date';
        hasErrors = true;
      }
      
      // Warn if issue date is very old (>2 years)
      if (issueDate) {
        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
        if (issueDate < twoYearsAgo) {
          toast.error('Warning: Issue date is more than 2 years old. Please verify the date is correct.', {
            duration: 5000
          });
        }
      }
    }
    
    if (hasErrors) {
      setValidationErrors(newErrors);
      toast.error('Please fix the errors in the form');
      return;
    }
    
    // Prepare submission data
    const submissionData = {
      leave_type_id: formData.leave_type_id,
      activity_type: formData.activity_type === 'Others' ? formData.other_activity_type : formData.activity_type,
      serial_number: formData.serial_number.trim() || null,
      issue_date: formData.issue_date || null,
      leave_start_date: formData.leave_start_date,
      leave_end_date: formData.leave_end_date,
      leave_start_time_period: formData.leave_start_time_period,
      leave_end_time_period: formData.leave_end_time_period,
      leave_reason: formData.leave_reason,
      document_approval_id: documentData.id,
      leave_total_days: calculatedLeaveDays
    };
    
    if (!onSubmit) return;

    // Await the parent mutation so we can keep the button locked for the whole
    // round-trip and surface backend duplicate errors (e.g. a serial number that
    // was already submitted). On success the parent closes this modal.
    try {
      setIsSubmitting(true);
      await onSubmit(submissionData);
    } catch (err) {
      const data = err?.response?.data;
      const serialErr = Array.isArray(data?.serial_number) ? data.serial_number[0] : data?.serial_number;
      const nonField = Array.isArray(data?.non_field_errors) ? data.non_field_errors[0] : data?.non_field_errors;
      const message = serialErr || data?.error || nonField || data?.detail
        || 'Failed to submit NS leave application. Please try again.';
      toast.error(message, { duration: 6000 });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Replace document
  const handleReplaceDocument = async () => {
    // If there's an existing document that hasn't been submitted, delete it
    if (documentData?.id && uploadState === 'uploaded' && !documentData.reusedExisting) {
      try {
        await staffAPI.deleteNSDocumentApproval(documentData.id);
        toast.success('Previous document removed');
      } catch (error) {
        console.error('Failed to delete old document:', error);
        // Continue anyway - don't block user from replacing
        toast.error('Failed to remove previous document. You can delete it manually later.');
      }
    }
    
    // Cancel any ongoing OCR
    if (ocrTimeoutRef.current) {
      clearTimeout(ocrTimeoutRef.current);
      ocrTimeoutRef.current = null;
    }
    if (ocrAbortControllerRef.current) {
      ocrAbortControllerRef.current.abort();
    }
    
    // Cleanup blob URL
    if (documentBlobUrlRef.current) {
      URL.revokeObjectURL(documentBlobUrlRef.current);
      documentBlobUrlRef.current = null;
    }
    
    setUploadState('idle');
    setDocumentData(null);
    setDocumentBlobUrl(null);
    setUploadError(null);
    setDocumentError(null);
    setOcrProcessing(false);
    setLoadingDocument(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Retry loading PDF blob
  const handleRetryPdfLoad = () => {
    setDocumentError(null);
    if (documentData?.filePath) {
      loadDocumentBlob(documentData.filePath);
    }
  };
  
  const handleClose = () => {
    if (
      documentData
      && uploadState === 'uploaded'
      && documentData.id
      && !documentData.reusedExisting
    ) {
      setShowCloseConfirm(true);
      return;
    }
    doClose();
  };

  const handleConfirmClose = async () => {
    await safeDiscardAndClose({
      discardAction: () => staffAPI.deleteNSDocumentApproval(documentData.id),
      onBeforeClose: () => setShowCloseConfirm(false),
      onDiscardSuccess: () => toast.success('Document removed'),
      onClose: () => doClose(),
      failureMessage: 'Failed to remove document. You can delete it manually from your uploads.',
      logContext: 'Failed to delete NS document during modal close:',
    });
  };

  const doClose = () => {
    if (ocrTimeoutRef.current) {
      clearTimeout(ocrTimeoutRef.current);
      ocrTimeoutRef.current = null;
    }
    if (ocrAbortControllerRef.current) {
      ocrAbortControllerRef.current.abort();
    }
    
    setUploadState('idle');
    setDocumentData(null);
    setUploadError(null);
    setOcrProcessing(false);
    setFormData({
      leave_type_id: '',
      activity_type: 'In-Camp Training (ICT)',
      other_activity_type: '',
      serial_number: '',
      issue_date: '',
      leave_start_date: '',
      leave_end_date: '',
      leave_start_time_period: 'AM',
      leave_end_time_period: 'PM',
      leave_reason: 'National Service Leave'
    });
    setValidationErrors({});
    
    onClose();
  };
  
  if (!isOpen) return null;
  
  // Dynamic modal sizing: smaller for upload, larger for form view
  const modalSizeClass = uploadState === 'uploaded' && documentData
    ? 'modal-container-xl'  // Large for split-screen form
    : 'modal-container-lg'; // Smaller for upload dropzone
  
  return (
    <Portal>
      <div className="modal-overlay">
        <div className={`modal-container ${isMobile ? 'w-full h-full max-w-none max-h-none rounded-none' : modalSizeClass} flex flex-col`}>
          {/* Header */}
          <div className="modal-header">
            <h3 className="modal-title flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              National Service Leave Application
            </h3>
            <button
              onClick={handleClose}
              className="modal-close focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        
          {/* Content */}
          <div className={`flex-1 overflow-hidden flex flex-col ${uploadState === 'uploaded' && documentData ? 'p-0' : 'p-6 overflow-y-auto'}`}>
          {uploadState === 'idle' && (
            /* Upload State - Clean Button-Only Approach */
            <div className="space-y-4">
              {/* Leave Type Dropdown — consistent with SmartLeaveApplicationModal */}
              {onChangeLeaveType && leaveTypeOptions.length > 0 && (
                <div className="form-group">
                  <label className="form-label">
                    <Calendar className="inline w-4 h-4 mr-2" />
                    Leave Type
                  </label>
                  <FormSelect
                    value={selectedLeaveTypeId}
                    onChange={(newValue) => {
                      if (newValue && newValue !== selectedLeaveTypeId) {
                        onChangeLeaveType(newValue);
                      }
                    }}
                    options={leaveTypeOptions}
                    theme="employee"
                    placeholder="🔍 Select your leave type"
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">
                  <FileText className="inline w-4 h-4 mr-2" />
                  NS Call-up Order
                  <span className="text-red-500 ml-1">*</span>
                </label>
                
                <div className="space-y-4">
                    {/* Upload Options - Side by side on all screen sizes */}
                    <div className="grid grid-cols-2 gap-4">
                      
                      {/* Choose File Option */}
                      <div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={handleFileInputChange}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full bg-white hover:bg-gray-50 text-gray-700 rounded-xl py-4 px-3 border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all flex flex-col items-center gap-2"
                        >
                          <Upload className="w-6 h-6 text-blue-600" />
                          <span className="font-medium text-sm">Choose File</span>
                        </button>
                      </div>

                      {/* Use Camera Option */}
                      <div>
                        <input
                          ref={cameraInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileSelect(file);
                            // Reset input to allow selecting same file again
                            if (cameraInputRef.current) {
                              cameraInputRef.current.value = '';
                            }
                          }}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => cameraInputRef.current?.click()}
                          className="w-full bg-white hover:bg-gray-50 text-gray-700 rounded-xl py-4 px-3 border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all flex flex-col items-center gap-2"
                        >
                          <Smartphone className="w-6 h-6 text-blue-600" />
                          <span className="font-medium text-sm">Use Camera</span>
                        </button>
                      </div>
                    </div>

                    {/* File Requirements */}
                    <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                      <p className="text-xs font-medium text-yellow-800 mb-1">📋 Requirements:</p>
                      <ul className="text-xs text-yellow-700 space-y-1">
                        <li>• Clear image of NS Call-up Order</li>
                        <li>• All text must be readable</li>
                        <li>• Maximum file size: 5MB</li>
                        <li>• Accepted formats: JPG, PNG, PDF</li>
                      </ul>
                    </div>
                </div>
              </div>
            </div>
          )}
          
          {uploadState === 'uploading' && (
            /* Uploading State */
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <Loader className="w-12 h-12 mx-auto text-blue-600 animate-spin mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Uploading Document...
                </h3>
                <p className="text-sm text-gray-600">
                  Please wait while we process your NS Call-up Order
                </p>
              </div>
            </div>
          )}
          
          {uploadState === 'ocr_processing' && (
            /* OCR Processing State */
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <Loader className="w-12 h-12 mx-auto text-blue-600 animate-spin mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Extracting Details from Document
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Please wait while we extract information from your NS Call-up Order...
                </p>
                <p className="text-xs text-gray-500">
                  This may take up to 30 seconds. The form will appear once extraction is complete.
                </p>
              </div>
            </div>
          )}
          
          {uploadState === 'error' && (
            /* Error State */
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Upload Failed
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {uploadError || 'Unable to upload document. Please try again.'}
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => {
                      setUploadState('idle');
                      setUploadError(null);
                    }}
                    className="btn btn-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleClose}
                    className="btn btn-outline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {uploadState === 'uploaded' && documentData && (
            /* Split-Screen State */
            <div className={`flex-1 flex ${isMobile ? 'flex-col' : 'flex-row'} overflow-hidden min-h-0`}>
              {/* Mobile tab bar */}
              {isMobile && (
                <MobileVerificationTabs
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  documentLabel="Document"
                  formLabel="Leave Details"
                  isAdmin={false}
                />
              )}
              {/* Left Panel - Document Preview (70% on desktop, full height tab on mobile) */}
              <div className={`${isMobile ? (activeTab === 'document' ? 'flex-1' : 'hidden') : 'w-[70%]'} ${!isMobile ? 'border-r' : ''} border-gray-200 flex flex-col bg-gray-50 min-h-0`}>
                {!isMobile && (
                  <VerificationPanelHeader title="NS Call-up Order" icon={FileText} />
                )}
                <div className="flex-1 overflow-hidden p-4 flex flex-col min-h-0">
                  <div className="relative w-full flex-1 min-h-0">
                  {loadingDocument ? (
                    <div className="flex items-center justify-center h-full bg-white border rounded-lg">
                      <div className="text-center">
                        <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Loading document preview...</p>
                      </div>
                    </div>
                  ) : documentError ? (
                    <div className="flex items-center justify-center h-full bg-white border rounded-lg text-gray-500">
                      <div className="text-center p-6">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                        <p className="text-sm font-medium text-gray-900 mb-1">Failed to load document</p>
                        <p className="text-xs text-gray-600 mb-4 max-w-xs mx-auto">{documentError}</p>
                        <button
                          onClick={handleRetryPdfLoad}
                          className="btn btn-outline btn-sm"
                        >
                          Retry Loading
                        </button>
                      </div>
                    </div>
                  ) : documentData.type === 'image' && (documentBlobUrl || documentData.previewUrl) ? (
                    <div
                      ref={viewer.containerRef}
                      {...viewer.panHandlers}
                      className="relative w-full h-full overflow-hidden bg-white border rounded-lg flex items-center justify-center"
                      style={viewer.containerStyle}
                    >
                      <img
                        src={documentBlobUrl || documentData.previewUrl}
                        alt="NS Call-up Order"
                        className="select-none pointer-events-none"
                        draggable={false}
                        style={{
                          transform: viewer.imageTransform,
                          transformOrigin: 'center center',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          transition: 'transform 0.15s ease-out'
                        }}
                      />
                      <div
                        className="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 bg-white/95 border border-gray-200 rounded-full shadow-md p-1"
                        onMouseDown={e => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={viewer.zoomOut}
                          className="icon-button"
                          title="Zoom out"
                          aria-label="Zoom out"
                        >
                          <ZoomOut className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={viewer.zoomIn}
                          className="icon-button"
                          title="Zoom in"
                          aria-label="Zoom in"
                        >
                          <ZoomIn className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={viewer.reset}
                          className="icon-button"
                          title="Fit to panel"
                          aria-label="Fit to panel"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : documentBlobUrl ? (
                    <div className="w-full h-full border rounded-lg bg-white overflow-hidden" style={isMobile ? { height: '100%' } : { minHeight: '400px', height: '100%' }}>
                      <PdfViewer
                        src={documentBlobUrl}
                        title="NS Call-up Order"
                        fileName="ns_callup_order.pdf"
                        theme="employee"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p>Document preview not available</p>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>
              
              {/* Right Panel - Form Fields (30% on desktop, full height tab on mobile) */}
              <div className={`${isMobile ? (activeTab === 'form' ? 'flex-1' : 'hidden') : 'w-[30%]'} flex flex-col min-h-0`}>
                {!isMobile && (
                  <VerificationPanelHeader title="Leave Details" />
                )}
                <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-6 space-y-6" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}>
                <form id="ns-leave-form" onSubmit={handleSubmit} className="space-y-6">
                  {/* Leave Duration Fields */}
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <h3 className="text-base font-semibold text-gray-900 flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        Total Days
                      </h3>
                      {calcLoading ? (
                        <InfoBadge color="gray" label="Calculating…" size="md" />
                      ) : calcError ? (
                        <InfoBadge color="red" label="Error" size="md" />
                      ) : calculatedLeaveDays > 0 ? (
                        <InfoBadge
                          color="blue"
                          label={`${formatLeaveDays(calculatedLeaveDays)} ${pluralizeDays(calculatedLeaveDays)}`}
                          size="md"
                        />
                      ) : null}
                    </div>
                    {calcError && (
                      <p className="text-sm text-red-600">{calcError}</p>
                    )}
                    <LeaveExcludedDaysCallout
                      excludedDates={excludedLeaveDates}
                      leaveTypeName={
                        availableLeaveTypes.find(
                          (t) => String(t.id) === String(formData.leave_type_id),
                        )?.leave_type_name || 'National Service Leave'
                      }
                      audience="employee"
                    />
                    
                    {/* Start Date + AM/PM */}
                    <div className="form-group">
                      <label className="form-label">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <DatePickerField
                            theme="employee"
                            selected={formData.leave_start_date ? parseLocalISOToDate(formData.leave_start_date) : null}
                            onChange={(date) => {
                              setFormData(prev => ({
                                ...prev,
                                leave_start_date: date ? toLocalISODate(date) : ''
                              }));
                              setValidationErrors(prev => ({ ...prev, leave_start_date: null }));
                            }}
                            className={`input text-base pl-12 pr-3 w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                              validationErrors.leave_start_date ? 'border-red-500 focus-visible:ring-red-500' : ''
                            }`}
                            placeholderText="Select start date"
                            minDate={new Date()}
                            required
                            portalId="datepicker-portal"
                            popperClassName="datepicker-popper-high-z"
                            popperPlacement="bottom-start"
                          />
                        </div>
                        <div className="flex bg-gray-100 rounded-full p-1 w-24">
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, leave_start_time_period: 'AM' }))}
                            className={`flex-1 py-1 px-2 text-xs font-medium rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                              formData.leave_start_time_period === 'AM'
                                ? 'bg-blue-800 text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            AM
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, leave_start_time_period: 'PM' }))}
                            className={`flex-1 py-1 px-2 text-xs font-medium rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                              formData.leave_start_time_period === 'PM'
                                ? 'bg-blue-800 text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            PM
                          </button>
                        </div>
                      </div>
                      {validationErrors.leave_start_date && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.leave_start_date}</p>
                      )}
                    </div>
                    
                    {/* End Date + AM/PM */}
                    <div className="form-group">
                      <label className="form-label">
                        End Date <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <DatePickerField
                            theme="employee"
                            selected={formData.leave_end_date ? parseLocalISOToDate(formData.leave_end_date) : null}
                            onChange={(date) => {
                              setFormData(prev => ({
                                ...prev,
                                leave_end_date: date ? toLocalISODate(date) : ''
                              }));
                              setValidationErrors(prev => ({ ...prev, leave_end_date: null }));
                            }}
                            className={`input text-base pl-12 pr-3 w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                              validationErrors.leave_end_date ? 'border-red-500 focus-visible:ring-red-500' : ''
                            }`}
                            placeholderText="Select end date"
                            minDate={formData.leave_start_date ? parseLocalISOToDate(formData.leave_start_date) : new Date()}
                            required
                            portalId="datepicker-portal"
                            popperClassName="datepicker-popper-high-z"
                            popperPlacement="bottom-start"
                          />
                        </div>
                        <div className="flex bg-gray-100 rounded-full p-1 w-24">
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, leave_end_time_period: 'AM' }))}
                            className={`flex-1 py-1 px-2 text-xs font-medium rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                              formData.leave_end_time_period === 'AM'
                                ? 'bg-blue-800 text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            AM
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, leave_end_time_period: 'PM' }))}
                            className={`flex-1 py-1 px-2 text-xs font-medium rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                              formData.leave_end_time_period === 'PM'
                                ? 'bg-blue-800 text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            PM
                          </button>
                        </div>
                      </div>
                      {validationErrors.leave_end_date && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.leave_end_date}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* NS Activity Type */}
                  <div className="form-group">
                    <label className="form-label">
                      NS Activity Type
                    </label>
                    <FormSelect
                      value={formData.activity_type}
                      onChange={(val) => {
                        setFormData(prev => ({ ...prev, activity_type: val }));
                        setValidationErrors(prev => ({ ...prev, activity_type: null }));
                      }}
                      options={[
                        { value: '', label: 'Select NS activity type...' },
                        ...nsActivityTypes.map(type => ({ value: type, label: type }))
                      ]}
                      theme="employee"
                    />
                    {validationErrors.activity_type && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.activity_type}</p>
                    )}
                    {formData.activity_type === 'Others' && (
                      <div className="mt-2">
                        <input
                          type="text"
                          value={formData.other_activity_type || ''}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, other_activity_type: e.target.value }));
                            setValidationErrors(prev => ({ ...prev, other_activity_type: null }));
                          }}
                          placeholder="Please specify the NS activity type..."
                          className={`input text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                            validationErrors.other_activity_type ? 'border-red-500 focus-visible:ring-red-500' : ''
                          }`}
                          required
                        />
                        {validationErrors.other_activity_type && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.other_activity_type}</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Document Verification Fields */}
                  <div className="space-y-4">
                    {/* Call-up Serial Number */}
                    <div className="form-group">
                      <label className="form-label">
                        Call-up Serial Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.serial_number || ''}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, serial_number: e.target.value }));
                          setValidationErrors(prev => ({ ...prev, serial_number: null }));
                        }}
                        placeholder="Enter Serial Number from NS Call-Up Notice"
                        className={`input text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                          validationErrors.serial_number ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }`}
                        maxLength={100}
                        required
                      />
                      {validationErrors.serial_number && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.serial_number}</p>
                      )}
                    </div>

                    {/* Call-up Issue Date */}
                    <div className="form-group">
                      <label className="form-label">
                        Call-up Issue Date <span className="text-red-500">*</span>
                      </label>
                      <DatePickerField
                        theme="employee"
                        selected={formData.issue_date ? parseLocalISOToDate(formData.issue_date) : null}
                        onChange={(date) => {
                          setFormData(prev => ({
                            ...prev,
                            issue_date: date ? toLocalISODate(date) : ''
                          }));
                          setValidationErrors(prev => ({ ...prev, issue_date: null }));
                        }}
                        className={`input text-base w-full pl-12 pr-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                          validationErrors.issue_date ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }`}
                        placeholderText="Select issue date"
                        maxDate={new Date()}
                        required
                        portalId="datepicker-portal"
                        popperClassName="datepicker-popper-high-z"
                        popperPlacement="bottom-start"
                      />
                      {validationErrors.issue_date && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.issue_date}</p>
                      )}
                    </div>
                  </div>
                  
                </form>
                </div>

                {/* Sticky Footer with Action Buttons */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex-shrink-0">
                  <div className="flex gap-3 btn-row-spread">
                    <button
                      type="submit"
                      form="ns-leave-form"
                      disabled={isSubmitting}
                      className="btn btn-primary flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                      {isSubmitting && <Loader className="w-4 h-4 animate-spin" />}
                      {isSubmitting ? 'Submitting…' : 'Submit Application'}
                    </button>
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isSubmitting}
                      className="btn btn-outline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={showCloseConfirm}
        title="Discard Uploaded Document?"
        message="You have uploaded a document but haven't submitted the application. Closing now will remove the uploaded document. Do you want to continue?"
        confirmLabel="Discard & Close"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleConfirmClose}
        onCancel={() => setShowCloseConfirm(false)}
        isLoading={isDiscarding}
        theme="employee"
      />
    </Portal>
  );
};

export default NSLeaveApplicationModal;
